import { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";

export default function PlatformAdminCommercial() {
  const [flags, setFlags] = useState({});
  const [organisations, setOrganisations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");
  const [orgName, setOrgName] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");

  const load = async () => {
    setError("");
    try {
      const [f, o, c, b] = await Promise.all([
        api.get("/commercial/feature-flags"),
        api.get("/commercial/organisations").catch(() => ({ data: { organisations: [] } })),
        api.get("/commercial/campaigns").catch(() => ({ data: { campaigns: [] } })),
        api.get("/commercial/booking-requests").catch(() => ({ data: { bookings: [] } })),
      ]);
      setFlags(f.data.flags || {});
      setOrganisations(o.data.organisations || []);
      setCampaigns(c.data.campaigns || []);
      setBookings(b.data.bookings || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load commercial admin data.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createOrganisation = async () => {
    if (!orgName.trim()) return;
    await api.post("/commercial/organisations", { name: orgName.trim(), org_type: "partner" });
    setOrgName("");
    await load();
  };

  const createCampaign = async () => {
    if (!campaignName.trim() || !selectedOrgId) return;
    await api.post("/commercial/campaigns", {
      organisation_id: selectedOrgId,
      name: campaignName.trim(),
      landing_title: campaignName.trim(),
    });
    setCampaignName("");
    await load();
  };

  const setCampaignStatus = async (id, status) => {
    await api.post(`/commercial/campaigns/${id}/status?status=${status}`);
    await load();
  };

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto" data-testid="platform-admin-commercial">
      <h1 className="text-2xl font-semibold">Academy Commercial Admin</h1>
      {error ? <p className="text-destructive">{error}</p> : null}

      <section>
        <h2 className="text-lg font-medium mb-2">Feature flags</h2>
        <ul className="text-sm space-y-1">
          {Object.entries(flags).map(([k, v]) => (
            <li key={k}>{k}: {String(v)}</li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-medium">Organisations</h2>
        <div className="flex gap-2">
          <input className="border px-2 py-1" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Organisation name" />
          <button type="button" className="border px-3 py-1" onClick={createOrganisation}>Create</button>
        </div>
        <ul className="text-sm">{organisations.map((o) => <li key={o.id}>{o.display_name || o.name}</li>)}</ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-medium">QR campaigns</h2>
        <div className="flex gap-2 flex-wrap">
          <select className="border px-2 py-1" value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
            <option value="">Select organisation</option>
            {organisations.map((o) => <option key={o.id} value={o.id}>{o.display_name || o.name}</option>)}
          </select>
          <input className="border px-2 py-1" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="Campaign name" />
          <button type="button" className="border px-3 py-1" onClick={createCampaign}>Create campaign</button>
        </div>
        <ul className="space-y-2 text-sm">
          {campaigns.map((c) => (
            <li key={c.id} className="border p-2 rounded">
              <div>{c.name} · {c.status}</div>
              <div className="break-all">{c.join_url || `/join/${c.public_code}`}</div>
              <div className="flex gap-2 mt-2">
                <a href={`/api/commercial/campaigns/${c.id}/qr.png`} target="_blank" rel="noreferrer">QR PNG</a>
                <a href={`/api/commercial/campaigns/${c.id}/qr.svg`} target="_blank" rel="noreferrer">QR SVG</a>
                <button type="button" onClick={() => setCampaignStatus(c.id, "ACTIVE")}>Activate</button>
                <button type="button" onClick={() => setCampaignStatus(c.id, "PAUSED")}>Pause</button>
                <button type="button" onClick={() => setCampaignStatus(c.id, "REVOKED")}>Revoke</button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-medium mb-2">Booking requests</h2>
        <ul className="text-sm space-y-1">
          {bookings.map((b) => (
            <li key={b.id}>{b.id} · {b.status} · learner {b.learner_id}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
